import styles from "./TimeTracking.module.css";
import dayjs from "dayjs";
import { formatCurrencyDecimal } from "../../../helpers/currency";
import { WEEKLY_REGULAR_LIMIT, calcWeeklyPay, formatMinutesToHM, isWeekend } from "./weeklyPay";
import { Avatar, CloseIcon } from "./EntryModals";

const pct = (part, whole) => `${Math.max(0, Math.min(100, whole > 0 ? (part / whole) * 100 : 0))}%`;

const WeeklySummaryModal = ({ employee, dateRange, onClose }) => {
    const pay = calcWeeklyPay(employee);
    const currency = employee?.currency || 'USD';
    const money = (v) => formatCurrencyDecimal(v || 0, currency);
    const weekClosed = dayjs().isAfter(dayjs(dateRange?.end), 'day');

    const entries = [...(employee?.timeTracking || [])].sort((a, b) => (a?.date > b?.date ? 1 : -1));

    return (<div className={`${styles.modalOverlay}`} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
        <div className={`${styles.modal} ${styles.modalWide}`} role="dialog" aria-label={`Weekly overtime, ${employee?.name}`}>

            <div className={`${styles.modalHead}`}>
                <div className={`${styles.mhLeft}`}>
                    <Avatar employee={employee} />
                    <div>
                        <div className={`${styles.modalName}`}>{employee?.name} · Weekly overtime</div>
                        <div className={`${styles.modalDate}`}>{dayjs(dateRange?.start).format('ddd, MMM D')} – {dayjs(dateRange?.end).format('ddd, MMM D, YYYY')} · {weekClosed ? 'Week closed' : 'Week in progress'}</div>
                    </div>
                </div>
                <button className={`${styles.modalClose}`} onClick={onClose} aria-label="Close"><CloseIcon /></button>
            </div>

            <div className={`${styles.modalBody}`}>
                <div className={`${styles.sumTiles}`}>
                    <div className={`${styles.sumTile}`}>
                        <span className={`${styles.boxLbl}`}>Regular</span>
                        <span className={`${styles.sumTileVal}`}>{formatMinutesToHM(pay.regularMinutes, '0h')}</span>
                        <span className={`${styles.sumTileAmt}`}>{money(pay.regularWage)}</span>
                        <span className={`${styles.boxMuted}`}>@ {money(employee?.wage)}/hr</span>
                    </div>
                    <div className={`${styles.sumTile} ${styles.sumTileOt}`}>
                        <span className={`${styles.boxLbl} ${styles.textOt}`}>Overtime</span>
                        <span className={`${styles.sumTileVal} ${styles.textOt}`}>{formatMinutesToHM(pay.otMinutes, '0h')}</span>
                        <span className={`${styles.sumTileAmt}`}>{money(pay.otWage)}</span>
                        <span className={`${styles.boxMuted}`}>@ {money(employee?.ot_wage)}/hr</span>
                    </div>
                    <div className={`${styles.sumTile} ${styles.sumTileTotal}`}>
                        <span className={`${styles.boxLbl}`}>Total</span>
                        <span className={`${styles.sumTileVal}`}>{formatMinutesToHM(pay.regularMinutes + pay.otMinutes, '0h')}</span>
                        <span className={`${styles.sumTileAmt}`}>{money(pay.wageTotal)}</span>
                        <span className={`${styles.boxMuted}`}>Reg + OT</span>
                    </div>
                </div>

                <div className={`${styles.sumTable}`}>
                    <div className={`${styles.sumRow} ${styles.sumHeadRow}`}>
                        <div>Day</div><div>Worked</div><div>Regular</div><div>Overtime</div>
                    </div>
                    {entries.map(t => {
                        const d = pay.days[t?.date] || { worked: 0 };
                        const we = isWeekend(t?.date);
                        return (<div key={t?.date} className={`${styles.sumRow} ${we ? styles.sumRowWe : ''}`}>
                            <div className={`${styles.sumDay}`}>
                                <span>{dayjs(t?.date).format('ddd, MMM D')}</span>
                                {we && <span className={`${styles.pillWe}`}>WEEKEND</span>}
                            </div>
                            <div>{formatMinutesToHM(d.worked)}</div>
                            <div>{we ? '—' : formatMinutesToHM(d.worked)}</div>
                            <div className={`${styles.textOt}`}>{we ? formatMinutesToHM(d.worked) : '—'}</div>
                        </div>);
                    })}
                    {pay.weeklyOtMinutes > 0 && (<div className={`${styles.sumRow} ${styles.sumFinalRow}`}>
                        <div>Weekly OT · after Thu</div>
                        <div>—</div>
                        <div>−{formatMinutesToHM(pay.weeklyOtMinutes)}</div>
                        <div className={`${styles.textOt}`}>+{formatMinutesToHM(pay.weeklyOtMinutes)}</div>
                    </div>)}
                </div>

                <div className={`${styles.infoBox}`}>
                    <div className={`${styles.boxLbl}`}>How this week was calculated</div>
                    <div className={`${styles.boxRow}`}><span>Weekday hours (5 workdays)</span><span className={`${styles.boxRowVal}`}>{formatMinutesToHM(pay.weekdayMinutes, '0h')}</span></div>
                    <div className={`${styles.bar} ${styles.barWide} ${styles.barSplit}`}>
                        {pay.weekdayMinutes > WEEKLY_REGULAR_LIMIT ? (<>
                            <div className={`${styles.barFill}`} style={{ width: pct(WEEKLY_REGULAR_LIMIT, pay.weekdayMinutes) }} />
                            <div className={`${styles.barFillOt}`} style={{ width: pct(pay.weeklyOtMinutes, pay.weekdayMinutes) }} />
                        </>) : <div className={`${styles.barFill}`} style={{ width: pct(pay.weekdayMinutes, WEEKLY_REGULAR_LIMIT) }} />}
                    </div>
                    <div className={`${styles.boxRow} ${styles.boxMuted}`}>
                        <span>40h regular cap</span>
                        {pay.weeklyOtMinutes > 0
                            ? <span className={`${styles.textOt}`}>+{formatMinutesToHM(pay.weeklyOtMinutes)} weekly OT</span>
                            : <span>{formatMinutesToHM(WEEKLY_REGULAR_LIMIT - pay.weekdayMinutes, '0m')} left before OT</span>}
                    </div>
                    <div className={`${styles.boxRow}`}><span>Weekend hours (Sat + Sun)</span><span className={`${styles.boxRowVal} ${styles.textOt}`}>{formatMinutesToHM(pay.weekendOtMinutes, '0h')} OT</span></div>
                    <div className={`${styles.boxRow} ${styles.boxMuted} ${styles.boxRowTop}`}>
                        <span>{formatMinutesToHM(pay.weekdayMinutes, '0h')} weekday + {formatMinutesToHM(pay.weekendOtMinutes, '0h')} weekend</span>
                        <span>= {formatMinutesToHM(pay.regularMinutes, '0h')} Reg + {formatMinutesToHM(pay.otMinutes, '0h')} OT</span>
                    </div>
                </div>
            </div>

            <div className={`${styles.modalFooter}`}>
                <div className={`${styles.footerSpacer}`} />
                <button className={`${styles.btnPrimary}`} onClick={onClose}>Done</button>
            </div>
        </div>
    </div>);
}

export default WeeklySummaryModal;
